import { useState, useEffect, useMemo } from "react";
import { NavLink } from "react-router-dom";
import { Database, ChevronRight, ChevronDown, X, PanelLeftOpen, Star, BarChart3, Target, ShoppingCart, Bot, Trophy } from "lucide-react";

function TableLink({ table, onClickItem, accent = false }) {
  return (
    <NavLink to={`/admin/t/${table.schema}/${table.name}`}
      data-testid={`admin-tbl-${table.schema}-${table.name}`}
      onClick={onClickItem}
      className={({ isActive }) =>
        `flex items-center justify-between gap-2 px-3 py-1.5 text-sm transition ${
          isActive ? 'bg-[#0F1829] text-white' : (accent ? 'hover:bg-[#FFFBF0]' : 'hover:bg-[#F5F2EB]')
        }`}>
      <span className="truncate">{table.label}</span>
      <ChevronRight size={12} className="opacity-40 shrink-0" />
    </NavLink>
  );
}

function FavoritesBlock({ tables, onClickItem }) {
  if (!tables || tables.length === 0) return null;
  return (
    <div className="py-1 bg-gradient-to-r from-[#FFF9E6] to-[#FFFBF0] border-y border-[#F0E5C0]"
      data-testid="admin-section-favoritos">
      <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-[0.22em] text-[#7A5C00] font-bold flex items-center gap-1.5">
        <Star size={11} fill="currentColor" className="text-[#D4A017]" /> Favoritos
      </p>
      {tables.map((t) => <TableLink key={t.key} table={t} onClickItem={onClickItem} accent />)}
    </div>
  );
}

function CollapsibleGroup({ name, tables, onClickItem, defaultOpen = false, kind = "schema" }) {
  const [open, setOpen] = useState(defaultOpen);
  // Re-sync when defaultOpen changes (e.g. user types in search → all expand)
  useEffect(() => { setOpen(defaultOpen); }, [defaultOpen]);
  const isSection = kind === "section";
  return (
    <div className="py-1" data-testid={`admin-${kind}-${name}`}>
      <button type="button" onClick={() => setOpen(o => !o)}
        data-testid={`admin-${kind}-toggle-${name}`}
        className={`w-full flex items-center justify-between px-3 pt-2 pb-1 text-[10px] uppercase tracking-[0.18em] font-bold hover:bg-[#F5F2EB] transition-colors ${
          isSection ? 'text-[#0F1829]' : 'text-[#978D78]'
        }`}>
        <span>
          {name}
          <span className="ml-2 text-[9px] text-[#978D78] font-normal tracking-wider">({tables.length})</span>
        </span>
        {open
          ? <ChevronDown size={12} className="opacity-60 shrink-0" />
          : <ChevronRight size={12} className="opacity-60 shrink-0" />}
      </button>
      {open && (
        <div data-testid={`admin-${kind}-body-${name}`}>
          {tables.map((t) => <TableLink key={t.key} table={t} onClickItem={onClickItem} />)}
        </div>
      )}
    </div>
  );
}

function CollapsedSidebar({ onExpand }) {
  return (
    <div className="p-2 flex flex-col items-center gap-1">
      <NavLink to="/admin" end data-testid="admin-sidebar-home-collapsed" title="Dashboard"
        className={({ isActive }) => `w-10 h-10 flex items-center justify-center rounded ${isActive ? 'bg-[#0F1829] text-white' : 'hover:bg-[#F5F2EB] text-[#0F1829]'}`}>
        <Database size={16} />
      </NavLink>
      <NavLink to="/admin/marketing" data-testid="admin-sidebar-marketing-collapsed" title="Avaliação de Marketing"
        className={({ isActive }) => `w-10 h-10 flex items-center justify-center rounded ${isActive ? 'bg-[#0F1829] text-white' : 'hover:bg-[#F5F2EB] text-[#0F1829]'}`}>
        <BarChart3 size={16} />
      </NavLink>
      <NavLink to="/admin/attribution" data-testid="admin-sidebar-attribution-collapsed" title="Atribuição de Marketing"
        className={({ isActive }) => `w-10 h-10 flex items-center justify-center rounded ${isActive ? 'bg-[#0F1829] text-white' : 'hover:bg-[#F5F2EB] text-[#0F1829]'}`}>
        <Target size={16} />
      </NavLink>
      <NavLink to="/admin/checkout-funnel" data-testid="admin-sidebar-funnel-collapsed" title="Funil de Checkout"
        className={({ isActive }) => `w-10 h-10 flex items-center justify-center rounded ${isActive ? 'bg-[#0F1829] text-white' : 'hover:bg-[#F5F2EB] text-[#0F1829]'}`}>
        <ShoppingCart size={16} />
      </NavLink>
      <NavLink to="/admin/incentivos" data-testid="admin-sidebar-incentivos-collapsed" title="Campanhas de Incentivos"
        className={({ isActive }) => `w-10 h-10 flex items-center justify-center rounded ${isActive ? 'bg-[#0F1829] text-white' : 'hover:bg-[#F5F2EB] text-[#0F1829]'}`}>
        <Trophy size={16} />
      </NavLink>
      <NavLink to="/admin/ai" data-testid="admin-sidebar-ai-collapsed" title="Assistente IA"
        className={({ isActive }) => `w-10 h-10 flex items-center justify-center rounded ${isActive ? 'bg-[#0F1829] text-white' : 'hover:bg-[#F5F2EB] text-[#0F1829]'}`}>
        <Bot size={16} />
      </NavLink>
      <button type="button" onClick={onExpand} title="Expandir para ver tabelas"
        className="w-10 h-10 flex items-center justify-center rounded hover:bg-[#F5F2EB] text-[#0F1829]">
        <PanelLeftOpen size={14} />
      </button>
    </div>
  );
}

export default function AdminSidebar({
  collapsed, onExpand, mobileNav, setMobileNav,
  search, setSearch, filtered, tables, sectionGroups, schemas,
}) {
  const onClickItem = () => setMobileNav(false);

  // Quando há texto de filtro, todas as secções/schemas devem abrir para
  // o utilizador ver as ocorrências sem precisar de clicar em cada uma.
  const expandAll = useMemo(() => Boolean(search && search.trim()), [search]);

  // Favoritos: section especial "favoritos" sempre no topo, sem ser colapsável.
  const favorites = sectionGroups?.FAVORITOS || sectionGroups?.favoritos || [];

  // Restantes sections (excluindo favoritos) — ordem alfabética.
  const otherSectionNames = Object.keys(sectionGroups || {})
    .filter((s) => s.toLowerCase() !== "favoritos")
    .sort();

  return (
    <aside data-testid="admin-sidebar"
      className={`border-r border-[#E6E1D6] bg-white min-h-[calc(100vh-3.5rem)] overflow-y-auto fixed lg:static top-14 bottom-0 lg:bottom-auto left-0 z-40 transition-all duration-300 ${
        mobileNav ? "translate-x-0" : "-translate-x-full"
      } lg:translate-x-0 ${collapsed ? "lg:w-[56px] w-72" : "w-72"}`}>
      {collapsed ? (
        <CollapsedSidebar onExpand={onExpand} />
      ) : (
        <>
          <div className="p-3 border-b border-[#E6E1D6]">
            <div className="flex items-center justify-between mb-2 lg:hidden">
              <span className="text-[11px] uppercase tracking-[0.2em] text-[#978D78]">Tabelas</span>
              <button onClick={() => setMobileNav(false)} data-testid="admin-close-nav" className="p-1 text-[#0F1829]">
                <X size={18} />
              </button>
            </div>
            <NavLink to="/admin" end data-testid="admin-sidebar-home"
              onClick={onClickItem}
              className={({ isActive }) => `flex items-center gap-2 px-3 py-2 text-sm rounded mb-2 ${isActive ? 'bg-[#0F1829] text-white' : 'hover:bg-[#F5F2EB]'}`}>
              <Database size={14} /> Dashboard
            </NavLink>
            <NavLink to="/admin/marketing" data-testid="admin-sidebar-marketing"
              onClick={onClickItem}
              className={({ isActive }) => `flex items-center gap-2 px-3 py-2 text-sm rounded mb-2 ${isActive ? 'bg-[#0F1829] text-white' : 'hover:bg-[#F5F2EB]'}`}>
              <BarChart3 size={14} /> Avaliação de Marketing
            </NavLink>
            <NavLink to="/admin/attribution" data-testid="admin-sidebar-attribution"
              onClick={onClickItem}
              className={({ isActive }) => `flex items-center gap-2 px-3 py-2 text-sm rounded mb-2 ${isActive ? 'bg-[#0F1829] text-white' : 'hover:bg-[#F5F2EB]'}`}>
              <Target size={14} /> Atribuição de Marketing
            </NavLink>
            <NavLink to="/admin/checkout-funnel" data-testid="admin-sidebar-funnel"
              onClick={onClickItem}
              className={({ isActive }) => `flex items-center gap-2 px-3 py-2 text-sm rounded mb-2 ${isActive ? 'bg-[#0F1829] text-white' : 'hover:bg-[#F5F2EB]'}`}>
              <ShoppingCart size={14} /> Funil de Checkout
            </NavLink>
            <NavLink to="/admin/incentivos" data-testid="admin-sidebar-incentivos"
              onClick={onClickItem}
              className={({ isActive }) => `flex items-center gap-2 px-3 py-2 text-sm rounded mb-2 ${isActive ? 'bg-[#0F1829] text-white' : 'hover:bg-[#F5F2EB]'}`}>
              <Trophy size={14} /> Campanhas de Incentivos
            </NavLink>
            <NavLink to="/admin/ai" data-testid="admin-sidebar-ai"
              onClick={onClickItem}
              className={({ isActive }) => `flex items-center gap-2 px-3 py-2 text-sm rounded mb-2 ${isActive ? 'bg-[#0F1829] text-white' : 'hover:bg-[#F5F2EB]'}`}>
              <Bot size={14} /> Assistente IA
            </NavLink>
            <input type="text" placeholder="Filtrar tabelas..."
              value={search} onChange={(e) => setSearch(e.target.value)}
              data-testid="admin-table-filter"
              className="w-full px-3 py-2 text-sm border border-[#E6E1D6] rounded outline-none focus:border-[#0F1829]" />
            <p className="text-[10px] uppercase tracking-[0.15em] text-[#978D78] mt-2">
              {filtered.length} de {tables.length} tabelas
            </p>
          </div>

          {/* Favoritos — sempre visíveis no topo */}
          <FavoritesBlock tables={favorites} onClickItem={onClickItem} />

          {/* Outras secções especiais (ex.: GERAL) — colapsáveis */}
          {otherSectionNames.map((s) => (
            <CollapsibleGroup key={`sec-${s}`} name={s} tables={sectionGroups[s]}
              onClickItem={onClickItem} defaultOpen={expandAll} kind="section" />
          ))}

          {/* Schemas SQL — colapsáveis por defeito */}
          {Object.keys(schemas).sort().map((s) => (
            <CollapsibleGroup key={s} name={s} tables={schemas[s]}
              onClickItem={onClickItem} defaultOpen={expandAll} kind="schema" />
          ))}
        </>
      )}
    </aside>
  );
}
